import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, get, set, update, remove, onValue, off } from 'firebase/database';
import Swal from 'sweetalert2';
import {
  HeartIcon, ShoppingCartIcon, TrashIcon, ArrowRightIcon,
  TagIcon, SparklesIcon, InformationCircleIcon, XMarkIcon,
  ShoppingBagIcon, ExclamationTriangleIcon, ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  HeartIcon as HeartSolidIcon,
  CheckCircleIcon as CheckCircleSolid,
  ShoppingCartIcon as CartSolid,
} from '@heroicons/react/24/solid';

// ─── SWEETALERT Z-INDEX ──────────────────────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('swal-wishlist-z')) {
  const s = document.createElement('style');
  s.id = 'swal-wishlist-z';
  s.textContent = `.swal2-container{z-index:999999!important}.swal2-on-top{z-index:999999!important}`;
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2500, toast: true, position: 'top-end', showConfirmButton: false, customClass: { container: 'swal2-on-top' } });

// ─── LOCAL STORAGE HELPERS ───────────────────────────────────────────────────
const LS = {
  get:    (key, fb = null) => { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fb; } catch { return fb; } },
  set:    (key, val)       => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} },
  remove: (key)            => { try { localStorage.removeItem(key); } catch {} },
};

// ─── PRICE HELPERS ───────────────────────────────────────────────────────────
const isOfferActive = (offer) => {
  if (!offer || !offer.isActive) return false;
  const now = Date.now();
  if (!offer.startDate && !offer.endDate) return true;
  if (offer.startDate && offer.endDate)   return now >= offer.startDate && now <= offer.endDate;
  if (offer.startDate && !offer.endDate)  return now >= offer.startDate;
  if (!offer.startDate && offer.endDate)  return now <= offer.endDate;
  return true;
};

// Mirrors Shop.jsx resolvePrice — offer > member > regular
const resolvePrice = (product, isMember) => {
  const regular     = product.price;
  const offerOn     = isOfferActive(product.offer);
  const offerPrice  = product.offer?.price;
  const memberPrice = product.memberPrice;

  if (offerOn && offerPrice && offerPrice < regular) {
    const pct = Math.round(((regular - offerPrice) / regular) * 100);
    return { displayPrice: offerPrice, originalPrice: regular, label: `${pct}% OFF`, labelColor: '#dc2626', hasDiscount: true, isOffer: true };
  }
  if (isMember && memberPrice && memberPrice < regular) {
    const pct = Math.round(((regular - memberPrice) / regular) * 100);
    return { displayPrice: memberPrice, originalPrice: regular, label: 'Member', labelColor: '#800020', hasDiscount: true, isOffer: false };
  }
  return { displayPrice: regular, originalPrice: null, label: null, labelColor: null, hasDiscount: false, isOffer: false };
};

// ─── PRICE DISPLAY ───────────────────────────────────────────────────────────
const PriceDisplay = ({ product, isMember }) => {
  const { displayPrice, originalPrice, label, labelColor, hasDiscount } = resolvePrice(product, isMember);
  return (
    <div>
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-base font-extrabold" style={{ color: '#800020' }}>
          KES {displayPrice?.toLocaleString()}
        </span>
        {label && (
          <span className="text-[10px] font-bold text-white px-1.5 py-0.5 rounded-md" style={{ background: labelColor }}>
            {label}
          </span>
        )}
      </div>
      {hasDiscount && originalPrice && (
        <p className="text-xs text-gray-400 line-through">KES {originalPrice?.toLocaleString()}</p>
      )}
    </div>
  );
};

// ─── BEHAVIOR NOTICE BANNER ──────────────────────────────────────────────────
const BehaviorNotice = () => (
  <motion.div
    initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
    className="flex items-start gap-3 p-4 rounded-2xl border mb-6"
    style={{ background: '#fffbeb', borderColor: '#fde68a' }}
  >
    <InformationCircleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
    <div className="text-sm text-amber-800 space-y-1 leading-relaxed">
      <p className="font-bold">How your Wishlist works</p>
      <ul className="text-xs text-amber-700 space-y-0.5 list-disc list-inside">
        <li><strong>Move to Cart</strong> — removes from wishlist but keeps your like &amp; like count intact.</li>
        <li><strong>Unlike a product</strong> — removes it from your wishlist entirely and decrements the like count.</li>
        <li><strong>Like again after unliking</strong> — re-adds it to your wishlist.</li>
      </ul>
    </div>
  </motion.div>
);

// ─── WISHLIST CARD ───────────────────────────────────────────────────────────
const WishlistCard = ({ product, isMember, isInCart, onMoveToCart, onUnlike, moving }) => {
  const offerActive = isOfferActive(product.offer);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col"
      style={{ transition: 'box-shadow 0.2s' }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = '0 8px 24px rgba(128,0,32,0.10)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = ''}
    >
      {/* Image */}
      <div className="relative bg-gray-50 flex-shrink-0" style={{ height: 180 }}>
        {product.images?.[0]
          ? <img src={product.images[0]} alt={product.name} className="w-full h-full object-contain p-3" />
          : <div className="w-full h-full flex items-center justify-center"><TagIcon className="h-12 w-12 text-gray-200" /></div>
        }

        {/* Offer badge */}
        {offerActive && (
          <span className="absolute top-2.5 left-2.5 text-[10px] font-extrabold text-white px-2 py-0.5 rounded-lg shadow" style={{ background: '#dc2626' }}>
            SALE
          </span>
        )}

        {/* Like (heart) — red solid; click to unlike */}
        <button
          onClick={() => onUnlike(product)}
          className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white/90 shadow flex items-center justify-center transition-transform hover:scale-110"
          title="Unlike (removes from wishlist)"
        >
          <HeartSolidIcon className="h-4 w-4" style={{ color: '#ef4444' }} />
        </button>

        {/* In-cart badge */}
        {isInCart && (
          <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1 bg-green-600/90 backdrop-blur-sm rounded-lg px-2 py-0.5">
            <CheckCircleSolid className="h-3 w-3 text-white" />
            <span className="text-white text-[10px] font-bold">In Cart</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        <p className="text-sm font-bold text-gray-900 leading-snug mb-0.5 line-clamp-2">{product.name}</p>
        {product.brand && <p className="text-xs text-gray-400 mb-2 truncate">{product.brand}</p>}

        <div className="mb-3 mt-auto">
          <PriceDisplay product={product} isMember={isMember} />
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {/* Move to Cart */}
          <motion.button
            whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
            onClick={() => onMoveToCart(product)}
            disabled={moving === product.id}
            className="w-full py-2.5 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow"
            style={{
              background: isInCart
                ? 'linear-gradient(135deg,#16a34a,#15803d)'
                : 'linear-gradient(135deg,#800020,#b30000)',
            }}
          >
            {moving === product.id
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Moving…</>
              : isInCart
              ? <><CheckCircleSolid className="h-4 w-4" /> Already in Cart</>
              : <><ShoppingCartIcon className="h-4 w-4" /> Move to Cart</>
            }
          </motion.button>

          {/* Unlike */}
          <button
            onClick={() => onUnlike(product)}
            className="w-full py-2 rounded-xl text-xs font-semibold border border-gray-200 text-gray-500 hover:border-red-300 hover:text-red-500 hover:bg-red-50 transition-all flex items-center justify-center gap-1.5"
          >
            <HeartIcon className="h-3.5 w-3.5" />
            Unlike &amp; Remove
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// ─── GUEST NOTICE ────────────────────────────────────────────────────────────
const GuestNotice = ({ onLogin }) => (
  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
    className="rounded-2xl overflow-hidden mb-6"
    style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#800020 100%)' }}>
    <div className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.12)' }}>
        <HeartSolidIcon className="h-5 w-5 text-red-400" />
      </div>
      <div className="flex-1">
        <p className="text-white font-bold text-sm">Your wishlist is saved locally</p>
        <p className="text-white/60 text-xs mt-0.5">Sign in to sync your wishlist across devices and never lose your favourites.</p>
      </div>
      <button onClick={onLogin}
        className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-bold transition-all"
        style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1.5px solid rgba(255,255,255,0.3)' }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.25)'}
        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
      >
        Sign In <ArrowRightIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  </motion.div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN WISHLIST
// ═══════════════════════════════════════════════════════════════════════════════
const Wishlist = ({ onNavigateToShop, onNavigateToLogin, onNavigateToCart }) => {
  const { currentUser, userData } = useAuth();
  const isMember = !!(currentUser && userData?.membership?.status === 'active');

  const [wishlistItems, setWishlistItems] = useState([]);
  const [cart,          setCart]          = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [moving,        setMoving]        = useState(null);

  // ─── LOAD WISHLIST ─────────────────────────────────────────────────────────
  const loadWishlist = useCallback(async () => {
    setLoading(true);
    try {
      let wishlistProductIds = [];

      if (currentUser) {
        const wsSnap = await get(ref(database, `users/${currentUser.uid}/wishlist`));
        if (wsSnap.exists()) {
          wishlistProductIds = Object.entries(wsSnap.val())
            .filter(([, v]) => v.isWishlist === true)
            .map(([productId]) => productId);
        }
      } else {
        const lsWishlist = LS.get('wishlist', {});
        wishlistProductIds = Object.entries(lsWishlist)
          .filter(([, v]) => v.isWishlist === true)
          .map(([productId]) => productId);
      }

      if (wishlistProductIds.length === 0) {
        setWishlistItems([]);
        setLoading(false);
        return;
      }

      const productPromises = wishlistProductIds.map(async (id) => {
        try {
          const snap = await get(ref(database, `products/${id}`));
          if (snap.exists()) return { id, ...snap.val() };
          return null;
        } catch { return null; }
      });

      const products = (await Promise.all(productPromises)).filter(Boolean);
      setWishlistItems(products);
    } catch (e) { console.error('Wishlist load error:', e); }
    finally { setLoading(false); }
  }, [currentUser]);

  // ─── LOAD CART ─────────────────────────────────────────────────────────────
  const loadCart = useCallback(async () => {
    try {
      if (currentUser) {
        const snap = await get(ref(database, `users/${currentUser.uid}/cart`));
        if (snap.exists()) {
          const val = snap.val();
          setCart(Array.isArray(val) ? val : Object.values(val));
        } else { setCart([]); }
      } else {
        setCart(LS.get('shop_cart', []));
      }
    } catch { setCart([]); }
  }, [currentUser]);

  useEffect(() => {
    loadWishlist();
    loadCart();
  }, [loadWishlist, loadCart]);

  // Real-time cart listener for logged-in users
  useEffect(() => {
    if (!currentUser) return;
    const cartRef = ref(database, `users/${currentUser.uid}/cart`);
    const unsub = onValue(cartRef, (snap) => {
      if (!snap.exists()) { setCart([]); return; }
      const val = snap.val();
      setCart(Array.isArray(val) ? val : Object.values(val));
    });
    return () => off(cartRef, 'value', unsub);
  }, [currentUser]);

  // ─── SAVE CART ─────────────────────────────────────────────────────────────
  const saveCart = async (updated) => {
    setCart(updated);
    if (currentUser) {
      await set(ref(database, `users/${currentUser.uid}/cart`), updated);
    } else {
      LS.set('shop_cart', updated);
    }
  };

  // ─── MOVE TO CART ──────────────────────────────────────────────────────────
  // 1. Resolve correct price (offer > member > regular)
  // 2. Add to cart (qty-additive if already there)
  // 3. DELETE the wishlist entry entirely (avoids isWishlist:false validation error)
  // 4. DO NOT touch likedProducts array — like stays, like count stays intact
  // 5. DO NOT touch productEngagement — engagement stays intact
  const moveToCart = async (product) => {
    setMoving(product.id);
    try {
      const { displayPrice } = resolvePrice(product, isMember);

      const cartItem = {
        id:            product.id,
        name:          product.name,
        price:         displayPrice,
        originalPrice: product.price,
        memberPrice:   product.memberPrice  || null,
        offerPrice:    product.offer?.price || null,
        quantity:      1,
        image:         product.images?.[0]  || null,
        variantType:   product.variantType  || 'simple',
      };

      // ── 1. Add to cart ──
      const currentCart = [...cart];
      const idx = currentCart.findIndex(i => i.id === cartItem.id);
      if (idx >= 0) {
        currentCart[idx].quantity += 1;
      } else {
        currentCart.push(cartItem);
      }
      await saveCart(currentCart);

      // ── 2. Remove wishlist entry entirely ──
      // We DELETE rather than setting isWishlist:false because the rule
      // enforces isWishlist === true on write — deletion bypasses this.
      // likedProducts and productEngagement are intentionally left untouched
      // so the like count and engagement record remain intact.
      if (currentUser) {
        await remove(ref(database, `users/${currentUser.uid}/wishlist/${product.id}`));
      } else {
        const lsWishlist = LS.get('wishlist', {});
        delete lsWishlist[product.id];
        LS.set('wishlist', lsWishlist);
      }

      // ── 3. Remove from UI ──
      setWishlistItems(prev => prev.filter(p => p.id !== product.id));
      toast('success', 'Moved to cart!', `${product.name} added to your cart`);
    } catch (e) {
      console.error('Move to cart error:', e);
      toast('error', 'Error', 'Could not move to cart. Please try again.');
    } finally {
      setMoving(null);
    }
  };

  // ─── UNLIKE ────────────────────────────────────────────────────────────────
  // 1. Confirm with user
  // 2. Remove from likedProducts array
  // 3. Delete wishlist entry entirely
  // 4. Remove productEngagement like record
  // 5. Decrement totalLikes on the product
  const unlikeProduct = async (product) => {
    const result = await Swal.fire({
      title: 'Unlike this product?',
      html: `
        <div style="text-align:center;padding:4px 0">
          <p style="color:#374151;font-size:14px;margin-bottom:6px">
            Unliking <strong>${product.name}</strong> will also remove it from your wishlist.
          </p>
          <p style="color:#6b7280;font-size:12px">You can always like it again to re-add it.</p>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Unlike & Remove',
      cancelButtonText: 'Keep',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      customClass: { container: 'swal2-on-top', popup: 'rounded-2xl' },
    });

    if (!result.isConfirmed) return;

    try {
      if (currentUser) {
        // Remove from likedProducts array
        const likesRef  = ref(database, `users/${currentUser.uid}/likedProducts`);
        const likesSnap = await get(likesRef);
        const fbLikes   = likesSnap.exists()
          ? (Array.isArray(likesSnap.val()) ? likesSnap.val() : Object.values(likesSnap.val()))
          : [];
        await set(likesRef, fbLikes.filter(id => id !== product.id));

        // Delete wishlist entry entirely
        await remove(ref(database, `users/${currentUser.uid}/wishlist/${product.id}`));

        // Remove productEngagement record
        await remove(ref(database, `productEngagement/${product.id}/likes/${currentUser.uid}`));

        // Decrement totalLikes on product
        const productRef  = ref(database, `products/${product.id}`);
        const productSnap = await get(productRef);
        if (productSnap.exists()) {
          await update(productRef, { totalLikes: Math.max(0, (productSnap.val().totalLikes || 0) - 1) });
        }
      } else {
        // localStorage
        const liked = LS.get('liked_products', []);
        LS.set('liked_products', liked.filter(id => id !== product.id));

        const lsWishlist = LS.get('wishlist', {});
        delete lsWishlist[product.id];
        LS.set('wishlist', lsWishlist);

        // Decrement totalLikes on Firebase even for guests
        try {
          const productRef  = ref(database, `products/${product.id}`);
          const productSnap = await get(productRef);
          if (productSnap.exists()) {
            await update(productRef, { totalLikes: Math.max(0, (productSnap.val().totalLikes || 0) - 1) });
          }
          // Remove guest engagement record
          const engSnap = await get(ref(database, `productEngagement/${product.id}/likes`));
          if (engSnap.exists()) {
            const entries = engSnap.val();
            for (const [key, val] of Object.entries(entries)) {
              if (!val.userId) {
                await remove(ref(database, `productEngagement/${product.id}/likes/${key}`));
                break;
              }
            }
          }
        } catch {}
      }

      setWishlistItems(prev => prev.filter(p => p.id !== product.id));
      toast('success', 'Removed from wishlist');
    } catch (e) {
      console.error('Unlike error:', e);
      toast('error', 'Error', 'Could not unlike product. Please try again.');
    }
  };

  // ─── DERIVED ───────────────────────────────────────────────────────────────
  const isInCart = (productId) => cart.some(i => i.id === productId);

  // ─── LOADING ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f8f9fa' }}>
        <div className="flex flex-col items-center gap-3">
          <ArrowPathIcon className="h-10 w-10 animate-spin" style={{ color: '#800020' }} />
          <p className="text-sm text-gray-400 font-medium">Loading your wishlist…</p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg,#f8f9fa 0%,#f1f3f5 100%)' }}>

      {/* HEADER */}
      <div className="bg-white border-b border-gray-100 sticky top-0" style={{ zIndex: 40 }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#ef4444,#b91c1c)' }}>
              <HeartSolidIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-extrabold text-gray-900">My Wishlist</h1>
              <p className="text-xs text-gray-400">
                {wishlistItems.length === 0
                  ? 'No saved items'
                  : `${wishlistItems.length} saved item${wishlistItems.length !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
          <button onClick={onNavigateToShop}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all"
            style={{ borderColor: '#800020', color: '#800020' }}
            onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <SparklesIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Browse Products</span>
          </button>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-8">

        {/* Guest notice */}
        {!currentUser && <GuestNotice onLogin={onNavigateToLogin} />}

        {/* Behavior notice */}
        <BehaviorNotice />

        {/* Empty state */}
        {wishlistItems.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-24 text-center bg-white rounded-2xl border border-gray-100 shadow-sm">
            <div className="relative mb-6">
              <div className="w-28 h-28 rounded-full flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg,#fef2f2,#fee2e2)' }}>
                <HeartIcon className="h-14 w-14" style={{ color: '#fca5a5' }} />
              </div>
              <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-bold">0</div>
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Your wishlist is empty</h2>
            <p className="text-gray-500 text-sm mb-8 max-w-xs leading-relaxed">
              Browse products and tap the ♥ heart to save items you love. They'll appear here.
            </p>
            <motion.button whileHover={{ scale: 1.04, boxShadow: '0 8px 28px rgba(128,0,32,0.35)' }} whileTap={{ scale: 0.97 }}
              onClick={onNavigateToShop}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-2xl text-sm font-bold text-white shadow-lg"
              style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
              <SparklesIcon className="h-4 w-4" />
              Discover Products
              <ArrowRightIcon className="h-4 w-4" />
            </motion.button>
          </motion.div>
        ) : (
          <>
            {/* Stats bar */}
            <div className="flex items-center gap-3 flex-wrap mb-6">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ background: '#fef2f2', color: '#800020' }}>
                <HeartSolidIcon className="h-3.5 w-3.5 text-red-500" />
                {wishlistItems.length} item{wishlistItems.length !== 1 ? 's' : ''} saved
              </div>
              {wishlistItems.some(p => isInCart(p.id)) && (
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                  style={{ background: '#f0fdf4', color: '#16a34a' }}>
                  <CheckCircleSolid className="h-3.5 w-3.5" />
                  {wishlistItems.filter(p => isInCart(p.id)).length} already in cart
                </div>
              )}
              <button
                onClick={onNavigateToCart}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
                style={{ background: '#1f2937', color: '#fff' }}
                onMouseEnter={e => e.currentTarget.style.background = '#374151'}
                onMouseLeave={e => e.currentTarget.style.background = '#1f2937'}>
                <ShoppingCartIcon className="h-3.5 w-3.5" />
                View Cart
              </button>
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              <AnimatePresence mode="popLayout">
                {wishlistItems.map(product => (
                  <WishlistCard
                    key={product.id}
                    product={product}
                    isMember={isMember}
                    isInCart={isInCart(product.id)}
                    onMoveToCart={moveToCart}
                    onUnlike={unlikeProduct}
                    moving={moving}
                  />
                ))}
              </AnimatePresence>
            </div>

            {/* Bottom CTA */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button onClick={onNavigateToCart}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-2xl text-sm font-bold text-white shadow-lg"
                style={{ background: 'linear-gradient(135deg,#1f2937,#374151)' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <CartSolid className="h-4 w-4" />
                Go to Cart
              </button>
              <button onClick={onNavigateToShop}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-2xl text-sm font-bold border-2 transition-all"
                style={{ borderColor: '#800020', color: '#800020' }}
                onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <SparklesIcon className="h-4 w-4" />
                Continue Shopping
              </button>
            </div>
          </>
        )}
      </div>

      <style>{`
        .line-clamp-2{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;}
      `}</style>
    </div>
  );
};

export default Wishlist;